import { create } from 'zustand'
import { POI, POIColliderConfig, DEFAULT_COLLIDER } from '../config/cameraConfig'

interface POIHoverState {
  /** ID of the POI currently being hovered (collider or marker) */
  hoveredPOIId: string | null
  setHoveredPOIId: (id: string | null) => void

  /** Whether to show collider wireframes (dev mode toggle) */
  showColliderWireframes: boolean
  setShowColliderWireframes: (show: boolean) => void

  /** Live collider overrides from dev panel editing (before save) */
  colliderOverrides: Record<string, POIColliderConfig>
  setColliderOverride: (poiId: string, config: POIColliderConfig) => void
  clearColliderOverride: (poiId: string) => void

  /** Detected mesh names per POI (populated by useMeshHighlight) */
  detectedMeshes: Record<string, string[]>
  setDetectedMeshes: (poiId: string, meshNames: string[]) => void

  /** POI ID to proactively detect meshes for (set when dev panel selects a POI with wireframes on) */
  detectForPOIId: string | null
  setDetectForPOIId: (id: string | null) => void

  /** Whether material emissive highlight is enabled on POI hover */
  materialHighlightEnabled: boolean
  setMaterialHighlightEnabled: (enabled: boolean) => void

  /** Save materialHighlightEnabled to server */
  saveMaterialHighlight: () => Promise<void>
  /** Load materialHighlightEnabled from server */
  loadMaterialHighlight: () => Promise<void>

  /** Get effective collider config: override > poi.collider > DEFAULT_COLLIDER */
  getColliderForPOI: (poi: POI) => POIColliderConfig
}

export const usePOIHoverStore = create<POIHoverState>((set, get) => ({
  hoveredPOIId: null,
  setHoveredPOIId: (id) => set({ hoveredPOIId: id }),

  showColliderWireframes: false,
  setShowColliderWireframes: (show) => set({ showColliderWireframes: show }),

  colliderOverrides: {},
  setColliderOverride: (poiId, config) =>
    set((s) => ({
      colliderOverrides: { ...s.colliderOverrides, [poiId]: config },
    })),
  clearColliderOverride: (poiId) =>
    set((s) => {
      const next = { ...s.colliderOverrides }
      delete next[poiId]
      return { colliderOverrides: next }
    }),

  detectedMeshes: {},
  setDetectedMeshes: (poiId, meshNames) =>
    set((s) => ({
      detectedMeshes: { ...s.detectedMeshes, [poiId]: meshNames },
    })),

  detectForPOIId: null,
  setDetectForPOIId: (id) => set({ detectForPOIId: id }),

  materialHighlightEnabled: true,
  setMaterialHighlightEnabled: (enabled) => {
    set({ materialHighlightEnabled: enabled })
  },

  saveMaterialHighlight: async () => {
    console.log('[POIHoverStore] Save disabled in static mode')
  },

  loadMaterialHighlight: async () => {
    try {
      const res = await fetch('/data/pois.json')
      if (res.ok) {
        const data = await res.json()
        if (typeof data.materialHighlightEnabled === 'boolean') {
          set({ materialHighlightEnabled: data.materialHighlightEnabled })
        }
      }
    } catch {
      console.log('[POIHoverStore] Config not available, using default material highlight setting')
    }
  },

  getColliderForPOI: (poi) => {
    const override = get().colliderOverrides[poi.id]
    if (override) return override
    return poi.collider ?? DEFAULT_COLLIDER
  },
}))
